import React, { Component } from 'react';
import { connect } from 'react-redux';
import NetworkTest from 'opentok-network-test-js';
import * as OT from '@opentok/client';
import classNames from 'classnames';
import { SessionState } from '../../stores/reducers/session';
import { GetTokboxToken, GetSession } from '../../connection/opentok';
import WaitingIndicator from '../general-components/waiting-indicator';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import { ReportError } from '../../connection/errors';
import { log } from '../../utils/logger';

const BRANDLIVE_OPENTOK_API_KEY = process.env.REACT_APP_OPENTOK_API_KEY ?? '';

interface OwnProps {
	// props that will be passed to this component
	testDone: () => void;
}

interface OwnState {
	done: boolean;
	uploadSpeed: number;
	downloadSpeed: number;
	error: string;
	score: number;
}

interface StateProps {
	// the reducer you want to import into this component
	SessionReducer: SessionState;
}

interface IntermediateResults {
	audio: {
		bytesReceived: number;
		packetsLost: number;
		packetsReceived: number;
	},
	video: {
		bytesReceived: number;
		packetsLost: number;
		packetsReceived: number;
	},
	timestamp: number;
}

interface DispatchProps {
	// the actions you want to fire from this component
	// setGap: Function;
}

const mapStateToProps = (state: any, ownProps: any) => ({ ...state, ...ownProps });

// the actions you want to fire from this component
const mapDispatchToProps = (dispatch: Function) =>	({
	// setGap: (gap: number) => {
	// 	dispatch(setGap(gap));
	// }
});

type Props = StateProps & DispatchProps & OwnProps;

const levels = ['poor', 'poor', 'ok', 'ok', 'good', 'good', 'good'];

class SpeedTest extends Component<Props> {
	private session: string | undefined;

	private token: string | undefined;

	private lastBytesReceived: number = 0;

	private lastTimestamp: number = 0;

	private mounted: boolean = false;

	private otNetworkTest: NetworkTest | null = null;

	state: OwnState = {
		done: false,
		uploadSpeed: 0,
		downloadSpeed: 0,
		error: '',
		score: 0,
	};

	async componentDidMount() {
		this.mounted = true;
		const sessionResult = await GetSession();
		this.session = sessionResult.sessionId;
		const result = await GetTokboxToken(this.session!);
		this.token = result.token;
		if (!this.mounted) return;
		this.checkConnectivity();
	}

	checkConnectivity = async () => {
		try {
			this.setState({ done: false });
			this.lastBytesReceived = 0;

			// @ts-ignore
			this.otNetworkTest = new NetworkTest(OT, {
				apiKey: BRANDLIVE_OPENTOK_API_KEY,
				sessionId: this.session,
				token: this.token,
			});

			if (this.otNetworkTest) {
				const connectionResults = await this.otNetworkTest.testConnectivity();

				if (connectionResults && connectionResults.success) {
					this.lastTimestamp = Date.now();
					const speedResults = await this.otNetworkTest
						.testQuality((stats: IntermediateResults) => {
							if (!this.mounted) return;

							const elapsedSeconds = (stats.timestamp - this.lastTimestamp) / 1000;
							if (stats.audio && stats.video) {
								const totalBytes = (stats.audio.bytesReceived * 8)
									+ (stats.video.bytesReceived * 8);
								const kbps = ((totalBytes - this.lastBytesReceived) / elapsedSeconds) / 1024;
								this.setState({ downloadSpeed: kbps });
								this.lastBytesReceived = totalBytes;
								this.lastTimestamp = stats.timestamp;
							} else {
								console.log(stats);
							}
						});

					if (!this.mounted) return;

					this.setState({
						done: true,
						score: speedResults.video.mos,
						downloadSpeed: ((speedResults.video.bitrate ?? 0)
							+ (speedResults.audio.bitrate ?? 0)) / 1024,
					}, () => {
						log({ speed_test: this.state.downloadSpeed });
					});

					this.props.testDone();
				} else {
					if (!this.mounted) return;
					this.setState({ error: 'Unable to connect to video servers' });
				}
			}
		} catch (e) {
			console.error(e);
			let message = 'Unknown Error';

			if (e instanceof Error) {
				message = e.message;
				ReportError('', window.navigator.userAgent, e.stack, e.message).catch((ex) => {
					console.error(`${ex} - ${message}`);
				});
			}
			if (!this.mounted) return;
			this.setState({ error: 'Error checking speed' });
		}
	};

	componentWillUnmount = () => {
		this.mounted = false;
		if (this.otNetworkTest) {
			this.otNetworkTest.stop();
		}
		if (!this.state.done) {
			log({ speed_test_skipped: true });
		}
	};

	renderFeedback = () => {
		const score = Math.round(this.state.score);
		switch (score) {
			case 0:
			case 1:
			case 2: {
				return (
				<>
					<h5>Your connection may be a bit uneven</h5>
					<p>Consider improving your connection and trying again</p>
				</>
				);
			}
			case 3: {
				return (
				<>
					<h5>Your connection could be better</h5>
					<p>Consider improving your connection and trying again</p>
				</>
				);
			}
			case 4: {
				return (
				<>
					<h5>Your connection is perfect!</h5>
				</>
				);
			}
			default: {
				return (
					<></>
				);
			}
		}
	};

	render() {
		const offsetAmount = () => {
			const arcMin = -260;
			const perc = this.state.downloadSpeed / 1800;
			return Math.abs(260 + (arcMin * perc) + 10);
		};

		const intScore = Math.round(this.state.score);

		return (
			<div className="panel speed-test">
				<p className="header">Make sure your internet is flying at warp speed</p>

				<div className="svg-container">
					<svg width="230" height="112" viewBox="0 0 230 112" fill="none" xmlns="http://www.w3.org/2000/svg">
						<mask id="mask0" mask-type="alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="230" height="112">
							<path fillRule="evenodd" clipRule="evenodd" d="M0.0273438 112C1.36303 49.9178 52.3268 0 115 0C177.674 0 228.638 49.9178 229.974 112H221.959C220.371 54.2927 173.092 8 115 8C56.9093 8 9.63035 54.2927 8.04172 112H0.0273438Z" fill="#362A68" />
						</mask>
						<g mask="url(#mask0)">
							<path opacity="0.15" d="M115 230.004L115 0.00391996" stroke="#B2B2BF" />
							<path opacity="0.15" d="M214.594 172.503L15.4079 57.5029" stroke="#B2B2BF" />
							<path opacity="0.15" d="M172.5 214.597L57.5 15.4109" stroke="#B2B2BF" />
							<path opacity="0.15" d="M57.5 214.597L172.5 15.4109" stroke="#B2B2BF" />
							<path opacity="0.15" d="M15.4062 172.503L214.592 57.5029" stroke="#B2B2BF" />
						</g>
						<path opacity="0.5" fillRule="evenodd" clipRule="evenodd" d="M114 32C73.6656 32 40.1632 61.4005 34.2681 99.8711C33.2497 106.517 27.8974 112 21.1739 112C14.4504 112 8.92462 106.525 9.70966 99.8476C15.7836 48.1832 60.1065 8 114 8C167.894 8 212.216 48.1832 218.29 99.8476C219.075 106.525 213.55 112 206.826 112C200.103 112 194.75 106.517 193.732 99.8711C187.837 61.4005 154.334 32 114 32Z" fill="#1A1A1F" />
						<path
							style={{
								transform: 'translate(10px, 8px)',
								strokeWidth: 23,
								strokeLinecap: 'round',
								strokeDashoffset: offsetAmount(),
								strokeDasharray: 270,
								transition: '1s stroke-dashoffset',
								transitionTimingFunction: 'linear',
							}}
							stroke="url(#paint0_linear)"
							d="M 12.69166,92.508101 C 31.306095,-13.819808 176.27305,-14.947955 197.9899,91.379953" />
						<defs>
							<linearGradient id="paint0_linear" x1="16" y1="111.808" x2="55" y2="46.3076" gradientUnits="userSpaceOnUse">
								<stop stopColor="#019C00" />
								<stop offset="1" stopColor="#A2F078" />
							</linearGradient>
						</defs>
					</svg>
					<div className="text-overlay">
						<div className="meter">
							<span className="meter-zero">0</span>
							<span className="meter-one">300</span>
							<span className="meter-two">600</span>
							<span className="meter-three">900</span>
							<span className="meter-four">1200</span>
							<span className="meter-five">1500</span>
							<span className="meter-six">1800</span>
						</div>
						<div className="total">
							{!this.state.done ? this.state.downloadSpeed.toFixed(0) : 'Done'}
						</div>

					</div>
				</div>

				<div className="done-actions">
					{this.state.done && (
						<>
							{intScore < 4 ? (
								<button onClick={this.checkConnectivity} className="redo">
									<div style={{ marginTop: -1 }}>
										<Icon name={ICONS.REFRESH} color={COLORS.WHITE} size={24}></Icon>
									</div>
								</button>
							) : (
								<button onClick={this.checkConnectivity} className="success">
									<div className="success">
										<Icon name={ICONS.CHECK} color={COLORS.WHITE} size={24}></Icon>
									</div>
								</button>
							)}
						</>
					)}
				</div>

				<div className="result-data">
					<label>
						<Icon name={ICONS.DOWNLOAD_SPEED} color={COLORS.GRAY_PRESENTER} size={16}></Icon>
						Video Transfer Speed
					</label>
					<span>
						{this.state.done ? (
							<span className="download-data">{this.state.downloadSpeed.toFixed(0)}</span>
						) : (
							<WaitingIndicator />
						)}
						Kbps</span>
				</div>

				<div className="feedback">
					{this.state.done
						&& <div className={classNames('result', levels[intScore])}>
							{this.renderFeedback()}
						</div>
					}
				</div>
			</div>
		);
	}
}

export default
connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(SpeedTest);
